// apps/platform/src/components/TypeChipOrderTable.tsx
//
// KLUB-264 — the type catalog table IS the chip order of the app tab. Rows are
// every type of the kind (deprecated and disabled included, because they can
// still produce chips through the feed) plus one "Todos" row at its saved
// position. Dragging a row saves immediately (KLUB-6 pattern): the page's
// `types` state is reordered optimistically, further drags lock while the save
// is in flight, a failure rolls back with an error and a success offers
// "Desfazer", which re-sends the previous order and position.
//
// The "Todos" position lives in `app.<kind>_all_chip_position` and is read with
// useTenantSettings. Dragging stays locked until both that read and the type
// list have loaded, because a drop computed against a guessed position would
// overwrite the real one. A save shows its position optimistically (a local
// override); once no save is in flight, any change of the read value (the
// realtime echo of our save, or another operator's edit) drops the override so
// the table follows the stored position again.

import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  ALL_CHIP_MARKER,
  ALL_CHIP_VALUE,
  allChipPositionFromIndex,
  normalizeAllChipPosition,
  placeAllChip,
} from "@klubora/shared";
import { useTenantSettings } from "../hooks/useTenantSettings";
import { DataTable, type Column } from "./DataTable";
import { SortableProvider, SortableRow } from "./Sortable";
import { colors, fontSize, fontWeight, radius, space } from "../lib/tokens";

export interface OrderableType {
  id: string;
  label: string;
  sort_order: number;
}

/** A type column. `opensSelected` is true on the row the app tab opens on. */
export interface OrderColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T, opensSelected: boolean) => ReactNode;
  align?: "left" | "right";
  width?: string | number;
}

export type AllChipPositionKey = "activities_all_chip_position" | "benefits_all_chip_position";

interface TypeChipOrderTableProps<T extends OrderableType> {
  tenantId: string;
  /** The page's type list, in `sort_order` order. */
  types: T[];
  /** The type list is (re)loading. */
  loading: boolean;
  /** Setter of the page's `types` state — the optimistic reorder writes here. */
  onTypesChange: (next: T[]) => void;
  positionKey: AllChipPositionKey;
  reorder: (tenantId: string, ids: string[], allPosition: number | null) => Promise<{ error?: string }>;
  columns: OrderColumn<T>[];
  /** Key of the first column the "Todos" row spans (the label column). */
  allSpanFrom: string;
  /** Hint after "Todos", e.g. "mostra todas as atividades, de qualquer tipo". */
  allHint: string;
  onRowClick?: (row: T) => void;
}

interface UndoState {
  ids: string[];
  position: number | null;
}

const UNDO_VISIBLE_MS = 8000;

export function TypeChipOrderTable<T extends OrderableType>({
  tenantId, types, loading, onTypesChange, positionKey, reorder,
  columns, allSpanFrom, allHint, onRowClick,
}: TypeChipOrderTableProps<T>) {
  const settings = useTenantSettings(tenantId, "app");
  const readPosition = normalizeAllChipPosition(settings.values[positionKey]);
  // The position this table last saved, until the read catches up or moves on.
  // `undefined` = follow the read.
  const [override, setOverride] = useState<number | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoState | null>(null);
  // Refs so the async commit and the effect below see current values.
  const savingRef = useRef(false);
  const readPositionRef = useRef(readPosition);
  readPositionRef.current = readPosition;

  const position = override !== undefined ? override : readPosition;

  // A read change outside a save is authoritative: follow it.
  useEffect(() => {
    if (!savingRef.current) setOverride(undefined);
  }, [readPosition]);
  const ordered = placeAllChip(types, position);
  const keys = ordered.map((item) => (item === ALL_CHIP_MARKER ? ALL_CHIP_VALUE : item.id));
  const canDrag = !loading && !settings.loading && !settings.error && !saving;

  useEffect(() => {
    if (!undo) return;
    const timer = setTimeout(() => setUndo(null), UNDO_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [undo]);

  // commit / handleReorder / handleUndo are not memoized: nothing depends on
  // their identity.
  const commit = async (ids: string[], nextPosition: number | null, offerUndo: boolean) => {
    const prevTypes = types;
    const prevPosition = position;
    onTypesChange(applyOrder(types, ids));
    setOverride(nextPosition);
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    setUndo(null);
    const res = await reorder(tenantId, ids, nextPosition);
    savingRef.current = false;
    setSaving(false);
    // Keep the override only while the read differs from what we want shown.
    const settle = (value: number | null) =>
      setOverride(readPositionRef.current === value ? undefined : value);
    if (res.error) {
      onTypesChange(prevTypes);
      settle(prevPosition);
      setSaveError(`Não foi possível salvar a nova ordem. A tabela voltou à ordem anterior. (${res.error})`);
      return;
    }
    settle(nextPosition);
    if (offerUndo) setUndo({ ids: prevTypes.map((t) => t.id), position: prevPosition });
  };

  const handleReorder = (nextKeys: string[]) => {
    if (!canDrag) return;
    const ids = nextKeys.filter((key) => key !== ALL_CHIP_VALUE);
    void commit(ids, allChipPositionFromIndex(nextKeys.indexOf(ALL_CHIP_VALUE)), true);
  };

  const handleUndo = () => {
    if (!undo || saving) return;
    void commit(undo.ids, undo.position, false);
  };

  const labelOf = useCallback(
    (key: string) => (key === ALL_CHIP_VALUE ? "Todos" : types.find((t) => t.id === key)?.label),
    [types],
  );

  const headerColumns: Column<T>[] = [
    { key: "__grip", header: "", width: 44, render: () => null },
    { key: "__pos", header: "#", width: 32, render: () => null },
    ...columns.map((c) => ({ ...c, render: () => null })),
  ];

  if (loading || settings.loading) {
    return <DataTable<T> columns={headerColumns} rows={[]} loading />;
  }

  if (types.length === 0) {
    return (
      <DataTable<T>
        columns={headerColumns}
        rows={[]}
        empty="Nenhum tipo configurado. Crie um tipo para montar a faixa de chips do app."
      />
    );
  }

  const spanAt = Math.max(0, columns.findIndex((c) => c.key === allSpanFrom));

  return (
    <div>
      {settings.error && (
        <div role="alert" style={s.errorBox}>
          <span>Não foi possível carregar a ordem do app. A tabela fica bloqueada para arrastar.</span>
          <button type="button" onClick={settings.reload} style={s.retryBtn}>Tentar de novo</button>
        </div>
      )}
      {saveError && <div role="alert" style={s.errorBox}><span>{saveError}</span></div>}

      <div style={s.wrap}>
        <SortableProvider items={keys} onReorder={handleReorder} labelOf={labelOf} disabled={!canDrag}>
          <table style={s.table} aria-busy={saving}>
            <thead>
              <tr>
                {headerColumns.map((c) => (
                  <th key={c.key} style={{ ...s.th, textAlign: c.align ?? "left", width: c.width }}>{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ordered.map((item, index) =>
                item === ALL_CHIP_MARKER ? (
                  <AllRow
                    key={ALL_CHIP_VALUE}
                    index={index}
                    leadingCells={spanAt}
                    span={columns.length - spanAt}
                    hint={allHint}
                    opensSelected={index === 0}
                    dragDisabled={!canDrag}
                  />
                ) : (
                  <TypeRow
                    key={item.id}
                    row={item}
                    index={index}
                    columns={columns}
                    opensSelected={index === 0}
                    dragDisabled={!canDrag}
                    onClick={onRowClick}
                  />
                ),
              )}
            </tbody>
          </table>
        </SortableProvider>
      </div>

      {undo && (
        <div role="status" style={s.toast}>
          <span>Ordem salva.</span>
          <button type="button" onClick={handleUndo} disabled={saving} style={s.toastBtn}>Desfazer</button>
        </div>
      )}
    </div>
  );
}

/** The small indigo "abre selecionado" tag, placed after the type name. */
export function OpensSelectedTag() {
  return <span style={s.opens}>abre selecionado</span>;
}

// ── Rows ────────────────────────────────────────────────────────────────────
// Each row is a SortableRow, so the sortable hook runs once per row.

interface TypeRowProps<T extends OrderableType> {
  row: T;
  index: number;
  columns: OrderColumn<T>[];
  opensSelected: boolean;
  dragDisabled: boolean;
  onClick?: (row: T) => void;
}

function TypeRow<T extends OrderableType>({ row, index, columns, opensSelected, dragDisabled, onClick }: TypeRowProps<T>) {
  return (
    <SortableRow
      id={row.id}
      data-row-key={row.id}
      handleLabel={`Arraste para reordenar ${row.label}`}
      dragDisabled={dragDisabled}
      onClick={onClick ? () => onClick(row) : undefined}
      style={{ cursor: onClick ? "pointer" : "default" }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.background = colors.paper; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.background = "transparent"; } : undefined}
    >
      {(handle) => (
        <>
          <td style={s.gripCell}>{handle}</td>
          <td style={s.posCell}>{index + 1}</td>
          {columns.map((c) => (
            <td key={c.key} style={{ ...s.td, textAlign: c.align ?? "left" }}>
              {c.render(row, opensSelected)}
            </td>
          ))}
        </>
      )}
    </SortableRow>
  );
}

interface AllRowProps {
  index: number;
  leadingCells: number;
  span: number;
  hint: string;
  opensSelected: boolean;
  dragDisabled: boolean;
}

function AllRow({ index, leadingCells, span, hint, opensSelected, dragDisabled }: AllRowProps) {
  return (
    <SortableRow
      id={ALL_CHIP_VALUE}
      data-row-key={ALL_CHIP_VALUE}
      handleLabel="Arraste para reordenar Todos"
      dragDisabled={dragDisabled}
    >
      {(handle) => (
        <>
          <td style={{ ...s.gripCell, ...s.allCell }}>{handle}</td>
          <td style={{ ...s.posCell, ...s.allCell }}>{index + 1}</td>
          {Array.from({ length: leadingCells }).map((_, i) => (
            <td key={i} style={{ ...s.td, ...s.allCell }} />
          ))}
          <td colSpan={span} style={{ ...s.td, ...s.allCell }}>
            <span style={s.allLabel}>Todos</span>
            {opensSelected && <OpensSelectedTag />}
            <span style={s.allHint}>{hint}</span>
          </td>
        </>
      )}
    </SortableRow>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Puts `types` in the order of `ids` and renumbers sort_order 1..N, as the
 *  server does — without the renumber the "Todos" placement would read stale
 *  values and the table would jump back. Ids not in `types` are skipped. */
function applyOrder<T extends OrderableType>(types: T[], ids: string[]): T[] {
  const byId = new Map(types.map((t) => [t.id, t]));
  const next: T[] = [];
  for (const id of ids) {
    const t = byId.get(id);
    if (t) next.push({ ...t, sort_order: next.length + 1 });
  }
  return next;
}

const s: Record<string, CSSProperties> = {
  wrap: {
    background: colors.white,
    border: `1px solid ${colors.fog}`,
    borderRadius: radius.md,
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  table: {
    width: "100%",
    minWidth: 480,
    borderCollapse: "collapse",
    fontSize: fontSize.md,
  },
  th: {
    textAlign: "left",
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: colors.stone,
    padding: "12px 16px",
    background: colors.paper,
    borderBottom: `1px solid ${colors.fog}`,
  },
  td: {
    padding: "14px 16px",
    borderBottom: `1px solid ${colors.fog}`,
    color: colors.ink,
    verticalAlign: "middle",
  },
  gripCell: {
    width: 44,
    padding: "0 0 0 8px",
    borderBottom: `1px solid ${colors.fog}`,
    verticalAlign: "middle",
  },
  posCell: {
    width: 32,
    padding: "14px 8px",
    borderBottom: `1px solid ${colors.fog}`,
    color: colors.stone,
    fontSize: fontSize.md,
    fontFeatureSettings: '"tnum" on',
    verticalAlign: "middle",
  },
  allCell: {
    background: colors.paper,
    borderTop: `1px dashed ${colors.stoneSoft}`,
    borderBottom: `1px dashed ${colors.stoneSoft}`,
    paddingTop: 10,
    paddingBottom: 10,
  },
  allLabel: {
    fontWeight: fontWeight.medium,
    color: colors.ink,
  },
  allHint: {
    marginLeft: space.sm,
    color: colors.stone,
    fontSize: fontSize.sm,
  },
  opens: {
    marginLeft: space.sm,
    color: colors.indigo,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
  },
  errorBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.md,
    color: colors.error,
    fontSize: fontSize.sm,
    marginBottom: space.md,
    background: colors.errorSoft,
    padding: space.sm,
    borderRadius: radius.sm,
  },
  retryBtn: {
    flexShrink: 0,
    padding: "2px 10px",
    background: "transparent",
    border: `1px solid ${colors.error}`,
    borderRadius: radius.sm,
    color: colors.error,
    fontSize: fontSize.sm,
    cursor: "pointer",
  },
  toast: {
    marginTop: space.md,
    display: "inline-flex",
    alignItems: "center",
    gap: space.md,
    background: colors.graphite,
    color: colors.white,
    padding: `${space.sm}px ${space.md}px`,
    borderRadius: radius.sm,
    fontSize: fontSize.sm,
  },
  toastBtn: {
    background: "none",
    border: 0,
    padding: 0,
    color: colors.white,
    textDecoration: "underline",
    font: "inherit",
    cursor: "pointer",
  },
};
